/**
 * useMemberCommunities — the ONE "communities I belong to" set.
 *
 * Extracted from CommunitySwitcherRail in DEV-1458 (§7.7) so the My Impact
 * RAIL and the new My Impact SECTION HOME (/memberships) read the identical
 * set. Two surfaces answering "where do I belong?" from two copies of this
 * loop is exactly the divergence the §7 family keeps legislating against
 * (cf. DEV-1448's one-resolver rule).
 *
 * Semantics (unchanged from the rail's original memo — DEV-1372 / DEV-1413):
 *   • Source of truth: active community_memberships rows for this user
 *     (geo declarations home/operating AND org memberships, together).
 *   • Personal Workshops and unknown GUIDs are excluded BEFORE resolution —
 *     a workshop is never a belonging row, and letting one enter the landing
 *     walk made the resolver fall through to the top-active-geo-root fallback,
 *     manufacturing a phantom country row for every user.
 *   • DORMANT memberships (declared community inactive as a community, §8.4
 *     ruling 1) are DISPLAYED resolved to the nearest ACTIVE ancestor via the
 *     landing walk (DEV-1415 ruling condition 1). The membership row itself is
 *     never rewritten. Resolution dedupes naturally.
 */
import { useMemo } from 'react';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import { createLandingResolver } from './landingResolver';
import type {
  CommunityEntity,
  CommunityRelationship,
} from '../../Pages/Communities/communityIdentity';

export interface UseMemberCommunitiesInputs {
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
  currentUserGuid: string;
  /**
   * Off-switch for surfaces that mount app-wide (the rail) — pass false when
   * off-section so the loop and the landing walk don't run on every route.
   */
  enabled?: boolean;
}

export function useMemberCommunities({
  entities,
  relationships,
  currentUserGuid,
  enabled = true,
}: UseMemberCommunitiesInputs): CommunityEntity[] {
  const { communityMemberships } = useApiSecurityData();

  return useMemo<CommunityEntity[]>(() => {
    if (!enabled || !currentUserGuid) return [];
    const me = String(currentUserGuid).toUpperCase();

    const entityByGuid = new Map<string, CommunityEntity>();
    for (const e of entities || []) {
      const g = String((e as any).entity_guid || '').toUpperCase();
      if (g) entityByGuid.set(g, e);
    }

    const declaredGuids = new Set<string>();
    for (const m of communityMemberships || []) {
      if (
        String((m as any).user_guid || '').toUpperCase() === me &&
        String((m as any).status || 'active').toLowerCase() === 'active'
      ) {
        const g = String((m as any).community_entity_guid || '').toUpperCase();
        const ent = entityByGuid.get(g);
        if (!ent || (ent as any).personal_owner_user_guid) continue;
        declaredGuids.add(g);
      }
    }

    const resolve = createLandingResolver(entities as any, relationships as any);
    const resolvedGuids = new Set<string>();
    for (const g of declaredGuids) {
      const r = resolve(g);
      if (r) resolvedGuids.add(r.toUpperCase());
    }

    return (entities || []).filter(
      (c) =>
        resolvedGuids.has(c.entity_guid.toUpperCase()) &&
        c.is_active !== false &&
        !(c as any).personal_owner_user_guid,
    );
  }, [enabled, currentUserGuid, communityMemberships, entities, relationships]);
}

export default useMemberCommunities;
